import './main.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Header from '../../../organisms/header/header.js';
import decodeToken from '../../../../utils/decode-token';
import TableHac from '../../../organisms/role-admin/table-hac/table-hac';
import TablePma from '../../../organisms/role-admin/table-pma/table-pma';
import TableRetailer from '../../../organisms/role-admin/table-retailer/table-retailer';
import TableAdmin from '../../../organisms/role-admin/table-admin/table-admin';
import TableTranslationUsers from '../../../organisms/role-admin/table-translation-users/table-translation-users';
import NewCalendar from '../../../organisms/common/new-calendar';
import { useTranslation } from 'react-i18next';
import RoleRibbon from '../../../organisms/common/role-ribbon/role-ribbon';
import { getAdminMainTabs } from '../../../organisms/common/role-ribbon/role-ribbon.config';
import { canViewUsers, canAccessTranslators, canAccessLibrary } from '../../../../utils/role-utils';
import ModalNominateOnBehalf from '../../../organisms/role-admin/modal-nominate-on-behalf/modal-nominate-on-behalf';
import MainSectionHeader from '../../../organisms/common/main-section-header/main-section-header';
import MainTabPanel from '../../../organisms/common/main-tab-panel/main-tab-panel';
import MemberStatusResultsSummary from '../../../organisms/common/table/member-status-results-summary';
import { fetchTranslationUsers } from '../../../../api/translationUsersApi';

const loadTranslationUsers = async () => {
  const { data } = await fetchTranslationUsers();
  return data;
};

const ADMIN_TAB_ROUTES = {
  hac: '/admin/main/hac',
  pma: '/admin/main/pma',
  retailer: '/admin/main/retailer',
  admin: '/admin/main/admin',
  translator: '/admin/main/translator',
};

const INITIAL_RELOAD_STATE = {
  hac: true,
  pma: true,
  retailer: true,
  admin: true,
  translator: true,
};

const AdminMain = () => {
  const { t } = useTranslation();

  const token = sessionStorage.getItem('token') || null;
  const role = decodeToken(token).role;
  const loggedUser = JSON.parse(sessionStorage.getItem('loggedUser') || null);
  const canNominate = loggedUser?.nominate;
  const showTranslators = canAccessTranslators(role);

  const location = useLocation();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('hac');
  const [reloadTabs, setReloadTabs] = useState(INITIAL_RELOAD_STATE);
  const requestedHacStatus = new URLSearchParams(location.search).get('status');
  const initialHacStatus = ['active', 'nominated', 'exited'].includes(requestedHacStatus)
    ? requestedHacStatus
    : 'active';
  const [hacStatusTab, setHacStatusTab] = useState(initialHacStatus);
  const [hacSummary, setHacSummary] = useState({ total: 0, nominated: 0, active: 0 });
  const [openModal, setOpenModal] = useState(false);

  const reloadTab = useCallback((tab) => {
    setReloadTabs((current) => ({ ...current, [tab]: true }));
  }, []);

  useEffect(() => {
    if (location.pathname.includes('/admin/calendar')) {
      setActiveTab('calendar');
    } else if (location.pathname.includes('main/hac')) {
      setActiveTab('hac');
      reloadTab('hac');
    } else if (location.pathname.includes('main/pma')) {
      setActiveTab('pma');
      reloadTab('pma');
    } else if (location.pathname.includes('main/retailer')) {
      setActiveTab('retailer');
      reloadTab('retailer');
    } else if (location.pathname.includes('main/admin')) {
      setActiveTab('admin');
      reloadTab('admin');
    } else if (location.pathname.includes('main/translator')) {
      if (showTranslators) {
        setActiveTab('translator');
        reloadTab('translator');
      } else {
        navigate('/admin/main/hac', { replace: true });
      }
    }
  }, [location.pathname, reloadTab, showTranslators, navigate]);

  useEffect(() => {
    if (location.pathname.includes('main/hac')) {
      setHacStatusTab(initialHacStatus);
    }
  }, [initialHacStatus, location.pathname]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    reloadTab(tab);
    navigate(ADMIN_TAB_ROUTES[tab]);
  };

  const handleHacStatusTabClick = (status) => {
    setHacStatusTab(status);
    navigate(`/admin/main/hac?status=${status}`);
  };

  const handleDataLoaded = useCallback(() => {
    setReloadTabs((current) => ({ ...current, [activeTab]: false }));
  }, [activeTab]);

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const getLabel = (key, defaultValue) => t(key, { defaultValue });
  const topTabs = getAdminMainTabs({ role, calendarActive: activeTab === 'calendar' });
  const userTabs = [
    { id: 'hac', label: t('common-rrc-members') },
    { id: 'pma', label: t('admin-main-tabs-tab2') },
    { id: 'retailer', label: t('admin-main-tabs-tab3') },
    { id: 'admin', label: 'Admins' },
    ...(showTranslators
      ? [
        {
          id: 'translator',
          label: t('admin-main-tabs-tab5', { defaultValue: 'Translators' }),
        },
      ]
      : []),
  ];
  const nominateButton = canNominate ? (
    <button className="btn secondary" onClick={handleOpenModal}>
      <span className="icon-plus"></span> {t('admin-main-tab1-action')}
    </button>
  ) : null;
  const newPmaButton = (
    <a href="/admin/new-pma" className="btn secondary">
      + {t('admin-main-tab2-action')}
    </a>
  );
  const newRetailerButton = (
    <a href="/admin/new-retailer" className="btn secondary">
      + {t('admin-main-tab3-action')}
    </a>
  );
  const newAdminButton = (
    <a href="/admin/new-admin" className="btn secondary">
      + {t('admin-main-tab4-action')}
    </a>
  );
  const newTranslationUserButton = (
    <a href="/admin/new-translation-user" className="btn secondary">
      + {t('translation-user-create-action', { defaultValue: 'Create translation user' })}
    </a>
  );
  const hacSummaryPanel = (
    <div className="admin-main__hac-status-summary">
      <MemberStatusResultsSummary
        active={hacSummary.active || 0}
        nominated={hacSummary.nominated || 0}
        total={hacSummary.total || 0}
      />
    </div>
  );
  const renderUserSectionHeader = (action = null) => (
    <MainSectionHeader
      title={getLabel('common-users', 'Users')}
      tabs={userTabs}
      activeTab={activeTab}
      onTabClick={handleTabClick}
      action={action}
    />
  );

  return (
    <>
      {token && canViewUsers(role) ? (
        <div className="App scaffold-wide">
          <Header />
          <main>
            <RoleRibbon tabs={topTabs} navigate={navigate} translate={t} />

            <MainTabPanel active={activeTab === 'calendar'} className="calendar-tab">
              <NewCalendar />
            </MainTabPanel>

            <MainTabPanel active={activeTab === 'hac'}>
              {hacSummaryPanel}
              {renderUserSectionHeader(nominateButton)}
              <TableHac
                reload={reloadTabs.hac}
                onDataLoaded={handleDataLoaded}
                statusFilter={hacStatusTab}
                onStatusFilterChange={handleHacStatusTabClick}
                onSummaryChange={setHacSummary}
              />
            </MainTabPanel>

            <MainTabPanel active={activeTab === 'pma'}>
              {renderUserSectionHeader(newPmaButton)}
              <TablePma
                reload={reloadTabs.pma}
                onDataLoaded={handleDataLoaded}
              />
            </MainTabPanel>

            <MainTabPanel active={activeTab === 'retailer'}>
              {renderUserSectionHeader(newRetailerButton)}
              <TableRetailer
                reload={reloadTabs.retailer}
                onDataLoaded={handleDataLoaded}
              />
            </MainTabPanel>

            <MainTabPanel active={activeTab === 'admin'}>
              {renderUserSectionHeader(newAdminButton)}
              <TableAdmin
                reload={reloadTabs.admin}
                onDataLoaded={handleDataLoaded}
              />
            </MainTabPanel>

            {showTranslators && (
              <MainTabPanel active={activeTab === 'translator'}>
                {renderUserSectionHeader(newTranslationUserButton)}
                <TableTranslationUsers
                  loadTranslationUsers={loadTranslationUsers}
                  reload={reloadTabs.translator}
                  onDataLoaded={handleDataLoaded}
                />
              </MainTabPanel>
            )}
          </main>
          <ModalNominateOnBehalf open={openModal} onClose={handleCloseModal} />
        </div>
      ) : (
        <Navigate to={token && canAccessLibrary(role) ? '/admin/main/library' : '/login'} replace />
      )}
    </>
  );
};

export default AdminMain;
